/* global window */
// ============================================================
// SELSKAPSROLLER — styre, daglig leder og signeringsansvar
// ============================================================
// Enhetsregisteret er kilden til hvem som sitter i styret og hvem som er daglig
// leder. Registeret har ingen e-postadresser, saa e-post er alltid noe brukeren
// fyller inn selv og som maa overleve en ny henting fra registeret.
//
// Modulen er databasefri med vilje: den mapper og slaar sammen roller, og eier
// reglene for hvem som skal signere hvilket dokument. Nettverk og lagring ligger
// i `services/backend.jsx`.

const PlummeCompanyRoles = (() => {
  const t = (key, fallback, vars) => (window.PlummeI18n?.t ? window.PlummeI18n.t(key, fallback, vars) : fallback);

  // Rollekodene fra Enhetsregisteret vi har bruk for. Revisor (REVI),
  // regnskapsfoerer (REGN) og kontaktperson (KONT) har ingen signeringsrolle i
  // aarsregnskapet, og hentes derfor ikke inn.
  const REGISTRY_ROLE_MAP = {
    LEDE: 'chair',
    NEST: 'deputyChair',
    MEDL: 'boardMember',
    VARA: 'deputy',
    DAGL: 'generalManager',
    INNH: 'owner',
  };
  // Rekkefoelgen roller vises og signerer i.
  const ROLE_ORDER = ['chair', 'deputyChair', 'boardMember', 'deputy', 'generalManager', 'owner'];
  const BOARD_ROLES = ['chair', 'deputyChair', 'boardMember'];
  // Regnskapsloven § 3-5: aarsregnskapet skrives under av samtlige styremedlemmer
  // og daglig leder. Protokollene signeres av styret. Varamedlem signerer bare
  // naar hen faktisk moette, og det vet vi foerst naar moetedataene finnes.
  const DOCUMENT_SIGNER_ROLES = {
    pdf: [...BOARD_ROLES, 'generalManager', 'owner'],
    board: BOARD_ROLES,
    general: BOARD_ROLES,
  };

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const cleanText = (value) => String(value == null ? '' : value).trim();
  const cleanEmail = (value) => cleanText(value).toLowerCase();

  const roleLabel = (role) => {
    const labels = {
      chair: t('role.chair', 'Styrets leder'),
      deputyChair: t('role.deputyChair', 'Nestleder'),
      boardMember: t('role.boardMember', 'Styremedlem'),
      deputy: t('role.deputy', 'Varamedlem'),
      generalManager: t('role.generalManager', 'Daglig leder'),
      owner: t('role.owner', 'Innehaver'),
    };
    return labels[role] || role;
  };

  // Navn + foedselsdato identifiserer personen paa tvers av hentinger. Registeret
  // gir ingen stabil id, og navn alene skiller ikke to personer med samme navn.
  const personKey = (person) => {
    const name = cleanText(person?.name).toLocaleLowerCase('nb-NO').replace(/\s+/g, ' ');
    return `${name}|${cleanText(person?.bornAt)}`;
  };

  const primaryRole = (person) => ROLE_ORDER.find((role) => safeArray(person?.roles).includes(role)) || '';

  const sortPeople = (people) => [...people].sort((a, b) => {
    const rank = ROLE_ORDER.indexOf(primaryRole(a)) - ROLE_ORDER.indexOf(primaryRole(b));
    return rank !== 0 ? rank : cleanText(a.name).localeCompare(cleanText(b.name), 'nb-NO');
  });

  const normalizePerson = (person, index = 0) => {
    const name = cleanText(person?.name);
    if (!name) return null;
    const roles = ROLE_ORDER.filter((role) => safeArray(person?.roles).includes(role));
    return {
      id: cleanText(person?.id) || `role-${index}-${personKey(person).replace(/[^a-z0-9]+/gi, '-')}`,
      name,
      bornAt: cleanText(person?.bornAt),
      email: cleanEmail(person?.email),
      roles,
      source: person?.source === 'manual' ? 'manual' : 'brreg',
    };
  };

  const normalizePeople = (people) => sortPeople(safeArray(people).map(normalizePerson).filter(Boolean));

  // Enhetsregisteret svarer med rollegrupper (STYR, DAGL, ...) som hver har en
  // liste roller. Samme person kan staa i flere grupper — typisk styreleder som
  // ogsaa er daglig leder — og skal da bli én person med to roller.
  const mapRegistryRoles = (payload) => {
    const byPerson = new Map();
    safeArray(payload?.rollegrupper).forEach((group) => {
      safeArray(group?.roller).forEach((entry) => {
        if (entry?.avregistrert) return;
        const role = REGISTRY_ROLE_MAP[cleanText(entry?.type?.kode).toUpperCase()];
        if (!role) return;
        const person = entry?.person;
        // Rollen kan innehas av et selskap i stedet for en person. Da finnes
        // ingen `person`, og det er ikke noen vi kan sende signering til.
        if (!person || person.erDoed) return;
        const name = [cleanText(person?.navn?.fornavn), cleanText(person?.navn?.mellomnavn), cleanText(person?.navn?.etternavn)]
          .filter(Boolean)
          .join(' ');
        if (!name) return;
        const candidate = { name, bornAt: cleanText(person?.fodselsdato), roles: [role], source: 'brreg' };
        const key = personKey(candidate);
        const existing = byPerson.get(key);
        if (existing) existing.roles = Array.from(new Set([...existing.roles, role]));
        else byPerson.set(key, candidate);
      });
    });
    return normalizePeople(Array.from(byPerson.values()));
  };

  // Registeret eier hvem som har rollene; brukeren eier e-postene. En ny henting
  // skal derfor aldri slette en e-post brukeren har lagt inn, og aldri beholde en
  // person som ikke lenger staar oppfoert.
  const mergeRegistryRoles = (existing, fetched) => {
    const current = new Map(normalizePeople(existing).map((person) => [personKey(person), person]));
    const merged = normalizePeople(fetched).map((person) => {
      const previous = current.get(personKey(person));
      return normalizePerson({
        ...person,
        id: previous?.id || person.id,
        email: previous?.email || person.email,
      });
    });
    // Personer brukeren har lagt til selv staar ikke i registeret, og blir staaende.
    const manual = normalizePeople(existing).filter((person) => person.source === 'manual'
      && !merged.some((entry) => personKey(entry) === personKey(person)));
    return sortPeople([...merged, ...manual]);
  };

  // Samme person signerer én gang selv om hen har flere roller.
  const signersForDocument = (people, documentKey) => {
    const wanted = DOCUMENT_SIGNER_ROLES[documentKey];
    if (!wanted) return [];
    return sortPeople(normalizePeople(people).filter((person) => person.roles.some((role) => wanted.includes(role))));
  };

  const signersMissingEmail = (people, documentKey) =>
    signersForDocument(people, documentKey).filter((person) => !person.email);

  // Signeringen kan ikke sendes ut foer alle som skal signere har en e-post.
  const canRequestSignatures = (people, documentKey) => {
    const signers = signersForDocument(people, documentKey);
    return signers.length > 0 && signers.every((person) => person.email);
  };

  return {
    ROLE_ORDER,
    BOARD_ROLES,
    DOCUMENT_SIGNER_ROLES,
    roleLabel,
    personKey,
    primaryRole,
    normalizePerson,
    normalizePeople,
    mapRegistryRoles,
    mergeRegistryRoles,
    signersForDocument,
    signersMissingEmail,
    canRequestSignatures,
  };
})();

Object.assign(window, { PlummeCompanyRoles });
